import { fromEvent, merge, race, Subject, timer } from 'rxjs';
import { delay, distinctUntilChanged, filter, map, mapTo, startWith, switchMap, take, takeUntil } from 'rxjs/operators';

import { MatTooltip } from '@angular/material/tooltip';
import { coerceStringArray } from '@angular/cdk/coercion';
import { FlexibleConnectedPositionStrategy } from '@angular/cdk/overlay';
import { Directive, ElementRef, HostBinding, inject, Input, OnDestroy, OnInit } from '@angular/core';

@Directive({
  selector: '[zefInputTooltip]',
  standalone: false,
})
export class InputTooltip extends MatTooltip implements OnInit, OnDestroy {
  readonly el = inject(ElementRef);

  private get element(): HTMLElement {
    return this.el.nativeElement;
  }

  @HostBinding('class.mat-tooltip-trigger')
  readonly matTooltipTrigger = true;

  @Input('zefInputTooltip')
  set inputTooltip(message: string) {
    this.message = message;
  }

  private destroy$ = new Subject<void>();

  private hideFromInputTooltip = false;

  private fixedOverlay?: boolean;

  private baseTooltipClass?: string[];

  private readonly focusDebounce$ = race(
    timer(1).pipe(delay(1), mapTo(true)),
    fromEvent(this.element, 'focusin').pipe(mapTo(false)),
  ).pipe(
    take(1),
    filter((focusout) => focusout),
  );

  private readonly focusOut$ = fromEvent(this.element, 'focusout').pipe(
    switchMap(() => this.focusDebounce$),
    mapTo('hide'),
  );

  private readonly scrollPositioning$ = timer(1).pipe(
    filter(() => !!this._overlayRef),
    switchMap(
      () => (this._overlayRef?.getConfig().positionStrategy as FlexibleConnectedPositionStrategy).positionChanges,
    ),
    map(({ scrollableViewProperties }) =>
      !scrollableViewProperties.isOriginClipped && !scrollableViewProperties.isOverlayClipped ? 'in' : 'out',
    ),
    startWith('show'),
    distinctUntilChanged(),
    takeUntil(this.focusOut$),
  );

  private readonly focusIn$ = fromEvent(this.element, 'focusin').pipe(switchMap(() => this.scrollPositioning$));

  ngOnInit(): void {
    merge(this.focusIn$, this.focusOut$)
      .pipe(takeUntil(this.destroy$))
      .subscribe((showState: 'hide' | 'show' | 'out' | 'in') => {
        (this as any)._ngZone.run(() => {
          switch (showState) {
            case 'hide':
              return this.hide(0, true);
            case 'show':
              return this.show(0, void 0, true);
            case 'in':
              return this.scrolledIn();
            case 'out':
              return this.scrolledOut();
          }
        });
      });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();

    this.destroy$.next();
    this.destroy$.complete();
  }

  show(
    delayTime?: number,
    origin?: {
      x: number;
      y: number;
    },
    force?: boolean,
  ): void {
    if (force && !this._isTooltipVisible()) {
      super.show(delayTime, origin);

      if (this._tooltipInstance) {
        const hide = this._tooltipInstance.hide.bind(this._tooltipInstance);

        this._tooltipInstance.hide = () => {
          if (this.hideFromInputTooltip) {
            this.hideFromInputTooltip = false;
            hide(delayTime);
          }
        };

        if (!this.fixedOverlay && this._overlayRef) {
          this.fixedOverlay = true;

          (this._overlayRef.getConfig().positionStrategy as FlexibleConnectedPositionStrategy)
            .withPositions([{ ...this._getOrigin().main, ...this._getOverlayPosition().main }])
            .withPush(false);
        }
      }
    }
  }

  hide(delayTime?: number, force?: boolean): void {
    if (force) {
      this.hideFromInputTooltip = true;

      super.hide(delayTime);
    }
  }

  private scrolledIn(): void {
    if (this._isTooltipVisible()) {
      this.tooltipClass = [...this.getBaseTooltipClass()];
    }
  }

  private scrolledOut(): void {
    if (this._isTooltipVisible()) {
      this.tooltipClass = [...this.getBaseTooltipClass(), 'hide'];
    }
  }

  private getBaseTooltipClass(): string[] {
    if (!this.baseTooltipClass) {
      this.baseTooltipClass = this.normalizeTooltipClass(this.tooltipClass).filter((item) => item !== 'hide');
    }

    return this.baseTooltipClass;
  }

  private normalizeTooltipClass(tooltipClass: MatTooltip['tooltipClass']): string[] {
    if (tooltipClass instanceof Set) {
      return Array.from(tooltipClass);
    } else if (tooltipClass && typeof tooltipClass === 'object' && !Array.isArray(tooltipClass)) {
      return Object.keys(tooltipClass).filter((key) => !!tooltipClass[key]);
    } else {
      return coerceStringArray(tooltipClass);
    }
  }
}
